import { Suspense, useMemo, useRef, useState, useEffect, useCallback } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { OrbitControls, Bounds, Html, Environment, Lightformer, Text, TransformControls, useTexture } from "@react-three/drei";
import * as THREE from "three";
import { Part3D } from "@/pages/Part3D";
import { productFonts } from "@/lib/fonts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Cube, Check, NotePencil, Ruler, ArrowsClockwise, CircleNotch, ShoppingCart, PaintBrush, Warning, PencilSimple, FloppyDisk, ArrowsOutCardinal, ArrowClockwise, ArrowsOut, ArrowsIn, FolderSimple, Sparkle, Info, UploadSimple, WhatsappLogo } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { assetUrl, apiError } from "@/lib/api";
import { getGlbThumb } from "@/lib/glbThumb";
import { MATERIAL_FAMILIES, MATERIAL_PRESETS } from "@/lib/materials";
import { useTranslation } from "react-i18next";

const NONE = "__none__";
const ZONE_KEYS = ["top", "bottom", "left", "right", "center", "topright", "topleft"];
const ROLE_METAL = ["band", "logo", "metal"];
const asScale3 = (s) => (Array.isArray(s) ? s : [s || 1, s || 1, s || 1]);
const ENAMEL_SWATCHES = [
  { name: "Rojo", hex: "#9B1B1B" }, { name: "Azul Marino", hex: "#1B2A5B" },
  { name: "Negro", hex: "#111111" }, { name: "Verde", hex: "#1B5B2A" },
  { name: "Blanco", hex: "#F5F5F5" }, { name: "Vino", hex: "#5B1B2A" },
  { name: "Morado", hex: "#4B1B5B" }, { name: "Dorado", hex: "#C9A227" },
];

// Measures the assembled parts bounding box for a few frames so the engraving
// text can be positioned on the ring band, then stops to avoid per-frame cost.
function SizeProbe({ groupRef, depKey, onSize }) {
  const frames = useRef(0);
  const key = useRef(null);
  if (key.current !== depKey) { key.current = depKey; frames.current = 0; }
  useFrame(() => {
    if (frames.current > 45) return;
    frames.current++;
    const g = groupRef.current;
    if (!g) return;
    const box = new THREE.Box3().setFromObject(g);
    if (box.isEmpty()) return;
    const s = new THREE.Vector3(); box.getSize(s);
    const c = new THREE.Vector3(); box.getCenter(c);
    onSize({ size: { x: s.x, y: s.y, z: s.z }, center: { x: c.x, y: c.y, z: c.z } });
  });
  return null;
}

// A designer logo that comes pre-baked (its placement lives inside the geometry, so its
// transform fields are identity) can't hand a swapped-in logo a position. We detect that case…
const _isZeroish = (a) => !a || (Math.abs(a[0]) < 1e-4 && Math.abs(a[1]) < 1e-4 && Math.abs(a[2]) < 1e-4);
const _scaleOne = (s) => s == null || Math.abs((Array.isArray(s) ? s[0] : s) - 1) < 0.02;
const isBakedAnchor = (tpl) => !!tpl && (tpl.prepared || tpl.normalize === false) && _isZeroish(tpl.position) && _scaleOne(tpl.scale);

// --- Reverse-engineering the anchor's curvature (fit a cylinder to its mesh) ---
// Jacobi eigen-decomposition of a symmetric 3x3 matrix → eigenvectors sorted by |value| desc.
function jacobi3(A) {
  let a = [[A[0][0], A[0][1], A[0][2]], [A[1][0], A[1][1], A[1][2]], [A[2][0], A[2][1], A[2][2]]];
  let v = [[1, 0, 0], [0, 1, 0], [0, 0, 1]];
  for (let iter = 0; iter < 24; iter++) {
    let p = 0, q = 1, max = Math.abs(a[0][1]);
    if (Math.abs(a[0][2]) > max) { max = Math.abs(a[0][2]); p = 0; q = 2; }
    if (Math.abs(a[1][2]) > max) { max = Math.abs(a[1][2]); p = 1; q = 2; }
    if (max < 1e-9) break;
    const app = a[p][p], aqq = a[q][q], apq = a[p][q];
    const phi = 0.5 * Math.atan2(2 * apq, aqq - app);
    const c = Math.cos(phi), s = Math.sin(phi);
    for (let k = 0; k < 3; k++) {
      const akp = a[k][p], akq = a[k][q];
      a[k][p] = c * akp - s * akq; a[k][q] = s * akp + c * akq;
    }
    for (let k = 0; k < 3; k++) {
      const apk = a[p][k], aqk = a[q][k];
      a[p][k] = c * apk - s * aqk; a[q][k] = s * apk + c * aqk;
    }
    for (let k = 0; k < 3; k++) {
      const vkp = v[k][p], vkq = v[k][q];
      v[k][p] = c * vkp - s * vkq; v[k][q] = s * vkp + c * vkq;
    }
  }
  const eig = [0, 1, 2].map((i) => ({ val: a[i][i], vec: new THREE.Vector3(v[0][i], v[1][i], v[2][i]).normalize() }));
  eig.sort((x, y) => Math.abs(y.val) - Math.abs(x.val));
  return eig;
}
// Kåsa algebraic circle fit on 2D points → {cx, cy, r, residual}.
function fitCircle(us, ws) {
  const n = us.length;
  let Suu = 0, Suw = 0, Sww = 0, Suuu = 0, Swww = 0, Suww = 0, Swuu = 0, Su = 0, Sw = 0;
  for (let i = 0; i < n; i++) {
    const u = us[i], w = ws[i];
    Suu += u * u; Sww += w * w; Suw += u * w;
    Suuu += u * u * u; Swww += w * w * w; Suww += u * w * w; Swuu += w * u * u;
    Su += u; Sw += w;
  }
  const det = Suu * Sww - Suw * Suw;
  if (Math.abs(det) < 1e-12) return null;
  const b1 = 0.5 * (Suuu + Suww), b2 = 0.5 * (Swww + Swuu);
  const cx = (b1 * Sww - b2 * Suw) / det;
  const cy = (b2 * Suu - b1 * Suw) / det;
  const r = Math.sqrt(Math.max(1e-9, cx * cx + cy * cy + (Suu + Sww) / n));
  let residual = 0;
  for (let i = 0; i < n; i++) { const d = Math.hypot(us[i] - cx, ws[i] - cy) - r; residual += d * d; }
  return { cx, cy, r, residual: Math.sqrt(residual / n) };
}
// Fit a curved plate: returns centroid, outward front normal, up (=wrap/curved direction),
// and the wrap radius. Falls back to null if the fit is degenerate (→ caller uses AABB).
function fitCurvedPlate(pts) {
  const n = pts.length;
  if (n < 20) return null;
  const C = new THREE.Vector3();
  pts.forEach((p) => C.add(p)); C.multiplyScalar(1 / n);
  const M = [[0, 0, 0], [0, 0, 0], [0, 0, 0]];
  const d = new THREE.Vector3();
  pts.forEach((p) => {
    d.copy(p).sub(C);
    M[0][0] += d.x * d.x; M[0][1] += d.x * d.y; M[0][2] += d.x * d.z;
    M[1][1] += d.y * d.y; M[1][2] += d.y * d.z; M[2][2] += d.z * d.z;
  });
  M[1][0] = M[0][1]; M[2][0] = M[0][2]; M[2][1] = M[1][2];
  const eig = jacobi3(M);
  const thin = eig[2].vec, inplane = [eig[0].vec, eig[1].vec];
  let best = null;
  for (const [curved, axis] of [[inplane[0], inplane[1]], [inplane[1], inplane[0]]]) {
    const us = [], ws = [];
    pts.forEach((p) => { d.copy(p).sub(C); us.push(d.dot(curved)); ws.push(d.dot(thin)); });
    const cir = fitCircle(us, ws);
    if (!cir || !isFinite(cir.r) || cir.r < 1e-3 || cir.r > 1e4) continue;
    const span = Math.max(...us) - Math.min(...us);
    const norm = cir.residual / (span || 1);
    if (!best || norm < best.norm) best = { curved, axis, cir, norm };
  }
  if (!best) return null;
  const centerRel = best.curved.clone().multiplyScalar(best.cir.cx).add(thin.clone().multiplyScalar(best.cir.cy));
  const center3D = C.clone().add(centerRel);
  let front = C.clone().sub(center3D);
  if (front.lengthSq() < 1e-9) front.copy(thin);
  front.normalize();
  if (front.dot(C) < 0) front.negate();
  let up = best.curved.clone();
  up.sub(front.clone().multiplyScalar(up.dot(front)));
  if (up.lengthSq() < 1e-9) up.copy(best.axis);
  up.normalize();
  if (up.y < 0) up.negate();
  const x = new THREE.Vector3().crossVectors(up, front).normalize();
  const y = new THREE.Vector3().crossVectors(front, x).normalize();
  const e = new THREE.Euler().setFromRotationMatrix(new THREE.Matrix4().makeBasis(x, y, front.clone()));
  return { center: [C.x, C.y, C.z], euler: [e.x, e.y, e.z], front, radius: best.cir.r };
}

// Derive a logo orientation from the anchor's measured box: the THINNEST axis is the plate's
// front/back normal (point it outward, away from the ring centre); the LONGEST axis is the
// plate's vertical (point it "up"). This makes a swapped flat logo face outward & upright.
const orientFromBox = (center, size) => {
  const axes = [
    { v: new THREE.Vector3(1, 0, 0), s: size[0] },
    { v: new THREE.Vector3(0, 1, 0), s: size[1] },
    { v: new THREE.Vector3(0, 0, 1), s: size[2] },
  ].sort((a, b) => a.s - b.s);
  const c = new THREE.Vector3(center[0], center[1], center[2]);
  const front = axes[0].v.clone();
  if (front.dot(c) < 0) front.negate();               // outward from the ring centre
  const up = axes[2].v.clone();
  if (up.y < 0) up.negate();                            // toward the top of the ring
  const x = new THREE.Vector3().crossVectors(up, front).normalize();
  const y = new THREE.Vector3().crossVectors(front, x).normalize();
  const e = new THREE.Euler().setFromRotationMatrix(new THREE.Matrix4().makeBasis(x, y, front.clone().normalize()));
  return { euler: [e.x, e.y, e.z], front: front.clone().normalize(), finger: x.clone() };
};

// Hidden probe: loads the zone's anchor geometry once and reports its bounding box (centre +
// size) IN partsRef-local space so we know the zone's real location/scale even when it's
// baked into the mesh (independent of the <Bounds> auto-fit and the ring's view rotation).
function ZoneAnchorProbe({ slot, tpl, rootRef, onMeasure }) {
  const ref = useRef();
  const done = useRef(false);
  const frames = useRef(0);
  useFrame(() => {
    if (done.current || frames.current > 120) return;
    frames.current++;
    const g = ref.current, root = rootRef.current;
    if (!g || !root) return;
    const world = new THREE.Box3().setFromObject(g);
    if (world.isEmpty()) return;
    root.updateWorldMatrix(true, false);
    const inv = new THREE.Matrix4().copy(root.matrixWorld).invert();
    const local = new THREE.Box3();
    const mn = world.min, mx = world.max;
    for (const xs of [mn.x, mx.x]) for (const ys of [mn.y, mx.y]) for (const zs of [mn.z, mx.z]) {
      local.expandByPoint(new THREE.Vector3(xs, ys, zs).applyMatrix4(inv));
    }
    const s = new THREE.Vector3(); local.getSize(s);
    const c = new THREE.Vector3(); local.getCenter(c);
    if (s.x + s.y + s.z < 1e-5) return;
    // Reverse-engineer the curvature: sample the mesh vertices (partsRef-local) and fit a
    // curved plate → real centre, outward normal, wrap direction and radius.
    const pts = [];
    g.updateWorldMatrix(true, true);
    g.traverse((o) => {
      if (o.isMesh && o.geometry && o.geometry.attributes && o.geometry.attributes.position) {
        const pos = o.geometry.attributes.position;
        const m = new THREE.Matrix4().multiplyMatrices(inv, o.matrixWorld);
        const stride = Math.max(1, Math.floor(pos.count / 1500));
        const v = new THREE.Vector3();
        for (let i = 0; i < pos.count; i += stride) { v.fromBufferAttribute(pos, i).applyMatrix4(m); pts.push(v.clone()); }
      }
    });
    const fit = fitCurvedPlate(pts);
    done.current = true;
    onMeasure(slot, { center: [c.x, c.y, c.z], size: [s.x, s.y, s.z], m: Math.max(s.x, s.y, s.z), fit });
  });
  // Apply the anchor's OWN transform (position/rotation/scale) so we measure where the logo
  // actually sits in the ring — the offset is baked in the mesh, the tilt is on the part.
  return (
    <group visible={false} position={tpl.position || [0, 0, 0]} rotation={tpl.rotation || [0, 0, 0]} scale={tpl.scale || 1}>
      <group ref={ref}><Part3D part={{ model_url: tpl.model_url, role: "logo", normalize: false, autoOrient: false }} /></group>
    </group>
  );
}

function PartThumb3D({ part }) {
  return (
    <Canvas camera={{ position: [0, 0, 2.4], fov: 40 }} dpr={1} style={{ width: "100%", height: "100%" }}>
      <ambientLight intensity={0.7} />
      <directionalLight position={[3, 4, 5]} intensity={1.3} />
      <directionalLight position={[-3, 1, -2]} intensity={0.5} />
      <Suspense fallback={null}>
        <Bounds fit clip observe margin={1.2}>
          <Part3D part={{ ...part, position: [0, 0, 0], rotation: [0, 0, 0], bend: 0, centered: true }} colorOverride="#C9A15A" />
        </Bounds>
      </Suspense>
    </Canvas>
  );
}

function GlbThumb({ modelUrl, orient, className }) {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let alive = true;
    setFailed(false); setSrc(null);
    getGlbThumb(modelUrl, orient).then((u) => { if (alive) { u ? setSrc(u) : setFailed(true); } });
    return () => { alive = false; };
  }, [modelUrl, JSON.stringify(orient)]);
  if (src) return <img src={src} alt="" className={`${className} object-contain`} />;
  return (
    <div className={`${className} flex items-center justify-center text-neutral-300`}>
      {failed ? <Cube size={20} /> : <CircleNotch size={16} className="animate-spin" />}
    </div>
  );
}

// Flat 2D preview of a client-uploaded logo shown on the ring while Meshy builds the 3D.
function FlatDecal({ url }) {
  const tex = useTexture(assetUrl(url));
  return (
    <mesh>
      <planeGeometry args={[1, 1]} />
      <meshBasicMaterial map={tex} transparent alphaTest={0.5} side={THREE.DoubleSide} toneMapped={false} />
    </mesh>
  );
}


function LazyThumb({ className, children }) {
  const ref = useRef(null);
  const [show, setShow] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || show) return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { setShow(true); io.disconnect(); }
    }, { rootMargin: "200px" });
    io.observe(el);
    return () => io.disconnect();
  }, [show]);
  return <div ref={ref} className={className}>{show ? children : <div className="w-full h-full flex items-center justify-center"><CircleNotch size={16} className="animate-spin text-neutral-300" /></div>}</div>;
}

export default function Assembly3D(props) {
  // Not inside Wix/Shopify (no iframe/popup) → the order goes to the store's own Hephy cart.
  const inHost = typeof window !== "undefined" && (window.self !== window.top || !!window.opener);
  const hephySlug = props.storeSlug || (!props.demo && !inHost ? props.product?.store?.slug : null);
  return <Assembly3DInner {...props} storeMode={props.storeMode || !!hephySlug} storeSlug={hephySlug} />;
}

function Assembly3DInner({ product, demo = false, storeMode = false, storeSlug = null, promoPct = 0, promoCode = null, showcase = false, photoMode = false }) {
  const { t, i18n } = useTranslation();
  const [parts, setParts] = useState(product.model_parts || []);
  const metals = useMemo(() => {
    const mainFam = product.material_family || "metal";
    const VALID_IDS = new Set(["silver", "platinum",
      "gold10ky", "gold14ky", "gold18ky", "gold24ky",
      "gold10kw", "gold14kw", "gold18kw", "gold24kw",
      "gold10kr", "gold14kr", "gold18kr", "gold24kr"]);
    const arr = product.metals?.length ? [...product.metals] : [{ id: "silver", name: "Plata", hex: "#C0C0C0", price: 0, family: "metal" }];
    // Only offer materials of the product's main family (a fabric shirt must never show gold).
    const inFam = arr.filter((m) => (m.family || "metal") === mainFam);
    // If none were configured for this family, fall back to that family's default colors
    // (NOT to the leftover metal options) so the client always sees sensible choices.
    const base = inFam.length ? inFam : (mainFam === "metal" ? arr : (MATERIAL_PRESETS[mainFam] || []).map((m) => ({ ...m, family: mainFam, price: 0 })));
    // Keep priceable options; for the metal family also hide legacy karat-less gold ids.
    const valid = base.filter((m) => {
      if (mainFam === "metal" && !VALID_IDS.has(m.id) && !m.custom) return false;
      return (m.price > 0) || (m.market && m.factor > 0) || (product.price > 0);
    });
    const list = valid.length ? valid : base.filter((m) => mainFam !== "metal" || VALID_IDS.has(m.id));
    (list.length ? list : base).sort((a, b) => (a.id === "silver" ? -1 : b.id === "silver" ? 1 : 0)); // Plata primero
    return list.length ? list : base;
  }, [product.metals, product.price, product.material_family]);
  const [metalId, setMetalId] = useState(metals[0].id);
  const [metalPicked, setMetalPicked] = useState(false);
  const selMetal = metals.find((m) => m.id === metalId) || metals[0];
  const metalColor = { hex: selMetal.hex, name: selMetal.name, price: selMetal.price || 0 };
  const [zoneMetal, setZoneMetal] = useState({}); // slot -> metalId (per-logo material override)
  const [logoColors, setLogoColors] = useState({}); // slot -> hex (per-logo color, non-jewelry)
  const [logoGlobalColor, setLogoGlobalColor] = useState(product.logo_default_color || "#333333"); // global logo color
  const metalHexById = (id) => (metals.find((m) => m.id === id) || {}).hex;
  const metalNameById = (id) => (metals.find((m) => m.id === id) || {}).name;
  const [stoneColors, setStoneColors] = useState({});
  const [enamelColors, setEnamelColors] = useState({});

  // Zones are defined by logo template pieces (slot_anchor). Non-logo parts keep the old
  // grouping/slot machinery; logos are chosen per-zone from the collection pool.
  const { slots, groupUnits, baseParts, zones, decorParts } = useMemo(() => {
    const isBase = (p) => (p.slot || "base") === "base" || p.role === "band";
    const base = parts.filter(isBase);
    const logoParts = parts.filter((p) => p.role === "logo" && !isBase(p));
    // Zones: group logo parts by slot; the anchor (or first) defines the zone transform.
    const bySlot = {};
    logoParts.forEach((p) => { (bySlot[p.slot || "logo"] = bySlot[p.slot || "logo"] || []).push(p); });
    const zn = Object.entries(bySlot).map(([slot, arr], i) => {
      const tpl = arr.find((x) => x.slot_anchor) || arr[0];
      return { slot, tpl, label: tpl.zone_label ? tpl.zone_label : ZONE_KEYS.includes(slot) ? t(`customizer.zones.${slot}`) : t("customizer.zones.generic", { n: i + 1 }) };
    });
    // Stones & enamels are NOT pickable list items: they always render in 3D and are only
    // recoloured via the colour selectors, never listed as cards in the logo/parts area.
    const isDecor = (p) => p.role === "stones" || p.role === "enamel";
    const decor = parts.filter((p) => !isBase(p) && p.role !== "logo" && isDecor(p));
    const rest = parts.filter((p) => !isBase(p) && p.role !== "logo" && !isDecor(p));
    const gm = {}; const ungrouped = [];
    rest.forEach((p) => { if (p.group_id) { (gm[p.group_id] = gm[p.group_id] || []).push(p); } else { ungrouped.push(p); } });
    const sl = {};
    ungrouped.forEach((p) => { (sl[p.slot] = sl[p.slot] || []).push(p); });
    const units = Object.entries(gm).map(([gid, arr]) => ({
      gid, arr,
      name: (arr[0].name || "Conjunto").replace(/\s*\(copia\)/gi, "").trim() || "Conjunto",
      optional: arr.some((p) => p.optional),
    }));
    return { slots: sl, groupUnits: units, baseParts: base, zones: zn, decorParts: decor };
  }, [parts, i18n.resolvedLanguage]);

  // Each zone's template defines the canonical transform (position/rotation/scale/curve/
  // thickness); any logo chosen for that zone renders at this transform.
  const slotTemplates = useMemo(() => {
    const t = {};
    zones.forEach((zn) => { t[zn.slot] = zn.tpl; });
    return t;
  }, [zones]);

  // Measured real placement of each baked zone anchor (see ZoneAnchorProbe).
  const [zoneBox, setZoneBox] = useState({});
  const handleZoneBox = useCallback((slot, box) => {
    setZoneBox((prev) => {
      const p = prev[slot];
      if (p && p.fit && Math.abs(p.center[0] - box.center[0]) < 1e-4 && Math.abs(p.m - box.m) < 1e-4) return prev;
      return { ...prev, [slot]: box };
    });
  }, []);

  // Pool of logos the client can drop into ANY zone: all 3D logos from the ring's
  // allowed collections.
  const logoPool = useMemo(() => (product.logos || []).filter((l) => (photoMode ? l.url : (l.is_3d && l.model_url))), [product.logos, photoMode]);
  const hasLogo = (l) => !!(l && (photoMode ? (l.url || l.thumb_url) : l.model_url));
  const collTypeMap = useMemo(() => { const m = {}; (product.collections || []).forEach((c) => { m[c.id] = c.type || "university"; }); return m; }, [product.collections]);
  const universityCollections = useMemo(() => (product.collections || []).filter((c) => (c.type || "university") === "university"), [product.collections]);
  const [university, setUniversity] = useState(product.default_collection_id || "");
  // Color palettes come from the OWNER's palettes (product.stone_palette / enamel_palette),
  // overridden by the selected collection's custom palette if it has one. This shows ALL the
  // colors the store defined (not a hardcoded subset).
  const selColl = useMemo(() => (product.collections || []).find((c) => c.id === university), [product.collections, university]);
  const rawStonePalette = useMemo(() => (
    (selColl?.stones?.length ? selColl.stones : null) || (product.stone_palette?.length ? product.stone_palette : null) ||
    [{ name: "Diamante", hex: "#8fd0ff" }, { name: "Zafiro", hex: "#1E5AC8" }, { name: "Rubí", hex: "#C81E3A" }, { name: "Esmeralda", hex: "#1B7A3A" }]
  ), [selColl, product.stone_palette]);
  // Diamante is the default stone and must appear first in the list.
  const isDiamond = (s) => /diamante|diamond/i.test(s?.name || "") || (s?.hex || "").toLowerCase() === "#8fd0ff";
  const stonePalette = useMemo(() => {
    const arr = [...rawStonePalette];
    arr.sort((a, b) => (isDiamond(b) ? 1 : 0) - (isDiamond(a) ? 1 : 0));
    return arr;
  }, [rawStonePalette]);
  const defaultStoneHex = useMemo(() => (stonePalette.find(isDiamond) || stonePalette[0] || { hex: "#8fd0ff" }).hex, [stonePalette]);
  // Distinct stone sets in this product (a ring can have 2+ sets of stones in different colors).
  const stoneSets = useMemo(() => {
    const keys = [];
    (parts || []).forEach((p) => { if (p.role === "stones") { const k = String(p.stoneSet || "1"); if (!keys.includes(k)) keys.push(k); } });
    return keys;
  }, [parts]);
  const stoneHexFor = (setKey) => stoneColors[String(setKey)] || defaultStoneHex;
  const enamelPalette = useMemo(() => (
    (selColl?.enamels?.length ? selColl.enamels : null) || (product.enamel_palette?.length ? product.enamel_palette : null) || ENAMEL_SWATCHES
  ), [selColl, product.enamel_palette]);
  // Keep each stone set's color valid within the active palette; default to Diamante.
  useEffect(() => {
    if (!stonePalette.length) return;
    setStoneColors((prev) => {
      const next = { ...prev };
      stoneSets.forEach((k) => { if (!next[k] || !stonePalette.some((s) => s.hex === next[k])) next[k] = defaultStoneHex; });
      return next;
    });
  }, [stonePalette, stoneSets]); // eslint-disable-line
  // Hide logos that belong to OTHER universities; global collections (and uncollectioned
  // logos) are always visible. University logos appear only once the client picks their school.
  const visiblePool = useMemo(() => logoPool.filter((l) => {
    const t = collTypeMap[l.collection_id];
    if (t === "university") return l.collection_id === university;
    return true;
  }), [logoPool, collTypeMap, university]);
  const poolByCollection = useMemo(() => {
    const names = {}; const types = {};
    (product.collections || []).forEach((c) => { names[c.id] = c.name; types[c.id] = c.type || "university"; });
    const groups = {};
    visiblePool.forEach((l) => { const k = l.collection_id || "__none__"; (groups[k] = groups[k] || []).push(l); });
    // University/school (primary) collections first — selected one leads — then general, then uncategorised.
    const rank = (cid) => {
      if (cid && cid === university) return 0;
      if (types[cid] === "university") return 1;
      if (cid === "__none__") return 3;
      return 2;
    };
    return Object.entries(groups)
      .map(([cid, items]) => ({ cid, name: names[cid] || "General", items }))
      .sort((a, b) => rank(a.cid) - rank(b.cid) || String(a.name).localeCompare(String(b.name)));
  }, [visiblePool, product.collections, university]);

  // Per-zone chosen logo (defaults to the template's own logo so zones start filled).
  const [zoneLogo, setZoneLogo] = useState(() => {
    const z = {};
    zones.forEach((zn) => { z[zn.slot] = photoMode ? null : { id: "__tpl_" + zn.slot, model_url: zn.tpl.model_url, name: zn.tpl.name, thumb_url: zn.tpl.thumb_url, orient: zn.tpl.orient, prepared: !!(zn.tpl.prepared || zn.tpl.normalize === false), _default: true }; });
    return z;
  });
  const [activeZone, setActiveZone] = useState(zones[0]?.slot || null);
  const [emptyDialog, setEmptyDialog] = useState(null);
  // Admin live-edit (only for the logged-in owner): adjust zone template transforms in the
  // real client view and save. Clients never see these controls.
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminEdit, setAdminEdit] = useState(false);
  const [adminFullscreen, setAdminFullscreen] = useState(false);
  const [editMode, setEditMode] = useState("translate");
  const [saving, setSaving] = useState(false);
  const [viewRotDeg, setViewRotDeg] = useState(Number(product.view_rotation_y || 0));
  const zoneEditRef = useRef();
  useEffect(() => {
    let ok = true;
    (async () => {
      try {
        if (typeof localStorage === "undefined" || !localStorage.getItem("cf_token")) return;
        await api.get(`/products/${product.id}`); // owner-only endpoint → 200 means admin owns it
        if (ok) setIsAdmin(true);
      } catch { /* not admin / not owner */ }
    })();
    return () => { ok = false; };
  }, [product.id]);
  const [sel, setSel] = useState(() => {
    const s = {};
    Object.entries(slots).forEach(([slot, arr]) => {
      const anyOptional = arr.some((p) => p.optional);
      s[slot] = anyOptional ? NONE : arr[0].id;
    });
    groupUnits.forEach((u) => { s["group:" + u.gid] = u.gid; });
    return s;
  });
  const [engraving, setEngraving] = useState("");
  const [ringSize, setRingSize] = useState("");
  const [autoRotate, setAutoRotate] = useState(false);
  const fonts = productFonts(product);
  const [font, setFont] = useState(fonts[0]);
  const [dims, setDims] = useState(null);
  const [goldPlating, setGoldPlating] = useState(false);
  const [comments, setComments] = useState("");
  const [personalLogos, setPersonalLogos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const MAX_PERSONAL = 4;
  const usesCustomLogo = useMemo(
    () => Object.values(zoneLogo).some((l) => l?.personal),
    [zoneLogo]);

  const pollPersonal = (logoId) => {
    const tick = async () => {
      try {
        const { data } = await api.get(`/public/products/${product.id}/custom-logo/${logoId}`, { withCredentials: false });
        if (data.status === "done" && data.model_url) {
          setPersonalLogos((prev) => prev.map((l) => (l.id === logoId ? { ...l, model_url: data.model_url, status: "done" } : l)));
          setZoneLogo((z) => { const nz = { ...z }; Object.keys(nz).forEach((k) => { if (nz[k]?.id === logoId) nz[k] = { ...nz[k], model_url: data.model_url, status: "done" }; }); return nz; });
          toast.success(t("customizer.logo3dReady"));
          return;
        }
        if (data.status === "failed") {
          setPersonalLogos((prev) => prev.map((l) => (l.id === logoId ? { ...l, status: "failed" } : l)));
          toast.error(t("customizer.logoFailed"));
          return;
        }
      } catch { /* keep polling */ }
      setTimeout(tick, 5000);
    };
    setTimeout(tick, 4000);
  };

  const uploadPersonalLogo = (file) => {
    if (!file) return;
    if (personalLogos.length >= MAX_PERSONAL) { toast.info(t("customizer.maxPersonal", { max: MAX_PERSONAL })); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      setUploading(true);
      try {
        const name = (file.name || "Mi logo").replace(/\.[^.]+$/, "").slice(0, 40) || "Mi logo";
        if (photoMode) {
          const { data } = await api.post(`/public/products/${product.id}/upload-image`, { image: reader.result, name }, { withCredentials: false });
          const logo = { id: data.asset_id, name, url: data.url, thumb_url: data.url, personal: true, status: "done", collection_id: "__personal__" };
          setPersonalLogos((prev) => [...prev, logo]);
          if (activeZone) setZoneLogo((z) => ({ ...z, [activeZone]: logo }));
          return;
        }
        const { data } = await api.post(`/public/products/${product.id}/custom-logo`, { image: reader.result, name }, { withCredentials: false });
        const logo = { id: data.logo_id, name: data.name || name, url: data.url, thumb_url: data.thumb_url, model_url: null, is_3d: true, status: "processing", personal: true, collection_id: "__personal__" };
        setPersonalLogos((prev) => [...prev, logo]);
        if (activeZone) setZoneLogo((z) => ({ ...z, [activeZone]: logo }));
        pollPersonal(data.logo_id);
        toast.info(t("customizer.processing3d"));
      } catch (err) { toast.error(apiError(err) || t("customizer.uploadFailed")); }
      finally { setUploading(false); }
    };
    reader.readAsDataURL(file);
  };

  const glRef = useRef(null);
  const partsRef = useRef(null);
  // Precio por mercado (pennyweight): dwt × talla × factor, cuando el metal lo usa.
  const sizeNum = parseFloat(String(ringSize || "").replace(",", "."));
  const marketPrice = (selMetal.market && selMetal.factor && product.dwt && sizeNum > 0)
    ? product.dwt * sizeNum * selMetal.factor : null;
  const price = (marketPrice != null ? marketPrice : (selMetal.price > 0 ? selMetal.price : (product.price || 0)))
    + (goldPlating && product.gold_plating_enabled ? (product.gold_plating_price || 0) : 0)
    + (engraving && engraving.trim() && product.engraving_enabled ? (product.engraving_price || 0) : 0)
    + (usesCustomLogo ? (product.customer_upload_price || 0) : 0);
  // Impuesto y envío de la tienda (configurados en Ajustes). Se muestran en el desglose;
  // al carrito de Wix se envía SOLO el subtotal (Wix aplica impuesto/envío en su checkout).
  const storeTaxRate = Number(product.store?.tax_rate || 0);
  const storeShipping = Number(product.store?.default_shipping || 0);
  const promoPercent = Math.max(0, Math.min(100, Number(promoPct || 0)));
  const discountAmount = Math.round(price * promoPercent) / 100;
  const netSubtotal = Math.max(0, price - discountAmount);
  const taxAmount = Math.round(netSubtotal * storeTaxRate) / 100;
  const totalWithTax = netSubtotal + taxAmount + storeShipping;
  const hasTaxShip = storeTaxRate > 0 || storeShipping > 0;
  const hasBreakdown = hasTaxShip || promoPercent > 0;
  const engravingCharge = (engraving && engraving.trim() && product.engraving_enabled) ? Number(product.engraving_price || 0) : 0;
  // El precio no aparece hasta que el cliente elija metal e ingrese talla.
  const needsSize = product.size_enabled;
  const sizeEntered = !!String(ringSize || "").trim();
  const priceReady = metalPicked && (!needsSize || sizeEntered) && (!selMetal.market || sizeNum > 0);

  const activeSlotParts = Object.entries(slots).flatMap(([slot, arr]) =>
    sel[slot] === NONE ? [] : arr.filter((p) => p.id === sel[slot]));
  const activeGroupParts = groupUnits.flatMap((u) => sel["group:" + u.gid] === NONE ? [] : u.arr);
  // One logo mesh per zone (chosen logo rendered at the zone template's transform). A
  // client's uploaded logo shows as a flat 2D decal until its Meshy 3D model is ready.
  const logoRenders = zones.map((zn) => {
    const chosen = zoneLogo[zn.slot];
    if (!chosen) return null;
    // defaultAnchor = the zone's own anchor logo is showing (render it exactly as authored).
    // Any other logo is a swap → effPart fits it into the zone's measured placement.
    const defaultAnchor = !!(chosen._default || chosen.model_url === zn.tpl.model_url);
    const prepared = !!(chosen.prepared || chosen.normalize === false);
    if (chosen.model_url) return { id: "zone-" + zn.slot, slot: zn.slot, role: "logo", model_url: chosen.model_url, name: chosen.name, prepared, defaultAnchor, orient: chosen.orient };
    if (chosen.personal && chosen.url) return { id: "zone-" + zn.slot, slot: zn.slot, role: "logo", _flat: true, flat_image_url: chosen.url, name: chosen.name };
    return null;
  }).filter(Boolean);
  const visible = [...baseParts, ...decorParts, ...activeSlotParts, ...activeGroupParts, ...logoRenders];
  const visibleKey = visible.map((p) => p.id + (p.model_url || "")).join("|");
  const showEngraving = product.engraving_enabled && product.engraving_project_3d !== false && (product.material_family || "metal") !== "fabric";
  const enamelParts = parts.filter((p) => p.role === "enamel");
  const embedded = typeof window !== "undefined" && window.self !== window.top;
  // Popup/new-tab mode: the customizer runs standalone on hephy3d.com, opened from the
  // store with window.open(). We post the cart to the opener window instead of the parent.
  const popupMode = typeof window !== "undefined" && !embedded && (
    (window.opener && window.opener !== window) ||
    new URLSearchParams(window.location.search).get("popup") === "1"
  );

  // When the client picks a university (or on first load with the product's default), swap the
  // Logo Central to that collection's OFFICIAL logo and paint the enamels with its OFFICIAL color.
  useEffect(() => {
    const coll = (product.collections || []).find((c) => c.id === university);
    if (!coll) return;
    if (coll.official_logo_id && slotTemplates.center) {
      const logo = (product.logos || []).find((l) => l.id === coll.official_logo_id && l.is_3d && l.model_url);
      if (logo) setZoneLogo((z) => ({ ...z, center: logo }));
    }
    if (coll.official_enamel) {
      setEnamelColors((prev) => { const n = { ...prev }; enamelParts.forEach((p) => { n[p.id] = coll.official_enamel; }); return n; });
    }
  // eslint-disable-next-line
  }, [university]);

  const colorFor = (p) => (
    p.role === "stones" ? stoneHexFor(String(p.stoneSet || "1"))
      : p.role === "enamel" ? (enamelColors[p.id] || p.color || "#9B1B1B")
      : p.role === "logo" ? (
          mainFamily !== "metal"
            ? (product.logo_color_mode === "per_logo"
                ? (logoColors[p.slot] || product.logo_default_color || p.color || "#333333")
                : (logoGlobalColor || product.logo_default_color || p.color || "#333333"))
            : (metalHexById(zoneMetal[p.slot]) || metalColor.hex)
        )
      : ROLE_METAL.includes(p.role) ? metalColor.hex
      : p.color
  );

  // Physical look (metalness/roughness) driven by the material family so non-jewelry
  // products render realistically (matte plastic/wood/fabric) instead of shiny metal.
  const mainFamily = product.material_family || "metal";
  const selFamily = selMetal.family || mainFamily;
  const matPropsFor = (part) => {
    // Enamel is painted/ceramic, never metal: render matte-satin (non-metallic) so the
    // client colours don't look like shiny metal.
    if (part.role === "enamel") return { m: 0.0, r: 0.55 };
    // Per-part override (e.g. a furniture wood frame covered in fabric).
    if (part.material && MATERIAL_FAMILIES[part.material] && part.material !== "metal") {
      const f = MATERIAL_FAMILIES[part.material];
      return { m: f.metalness, r: f.roughness };
    }
    // Non-metal main material: force the correct look on body/logo parts. Metal is left
    // untouched so the existing satin-logo tuning (no black logos) is preserved.
    if (selFamily !== "metal" && (part.role === "logo" || ROLE_METAL.includes(part.role))) {
      const f = MATERIAL_FAMILIES[selFamily] || MATERIAL_FAMILIES.metal;
      return { m: f.metalness, r: f.roughness };
    }
    return null;
  };

  // Apply the slot template: every part in an anchored slot is normalized (same footprint
  // + relief depth) and rendered at the anchor's transform, so any chosen logo fills the
  // zone consistently with the same depth as the reference.
  // In an anchored slot every chosen logo uses the zone's transform (position, rotation,
  // size, curve). Part3D auto-normalizes role "logo" so all logos share footprint + depth.
  const effPart = (p) => {
    const tpl = slotTemplates[p.slot];
    if (!tpl || p.role !== "logo") return p;
    const nudge = (pos) => { const l = Math.hypot(pos[0], pos[1], pos[2]) || 1, o = 0.012; return [pos[0] + (pos[0] / l) * o, pos[1] + (pos[1] / l) * o, pos[2] + (pos[2] / l) * o]; };
    const tplCurve = { bend: tpl.bend, bendAxis: tpl.bendAxis, bendX: tpl.bendX, bendY: tpl.bendY, bendZ: tpl.bendZ, curvesX: tpl.curvesX, curvesY: tpl.curvesY, curvesZ: tpl.curvesZ, reliefDepth: tpl.reliefDepth, taperTop: tpl.taperTop, taperBottom: tpl.taperBottom, taperLeft: tpl.taperLeft, taperRight: tpl.taperRight, sizeMode: tpl.sizeMode, fitAxis: tpl.fitAxis, ratioW: tpl.ratioW, ratioH: tpl.ratioH, absW: tpl.absW, absH: tpl.absH, absD: tpl.absD, sizeUnit: tpl.sizeUnit };
    const baked = isBakedAnchor(tpl);
    // The anchor's OWN geometry (the default logo) is rendered exactly as authored — baked
    // logos at origin (placement lives in the mesh), gizmo-placed logos at their transform.
    if (p.defaultAnchor) {
      const pos = baked ? [0, 0, 0] : (tpl.position || [0, 0, 0]);
      return { ...p, position: nudge(pos), rotation: tpl.rotation, scale: tpl.scale, normalize: !(tpl.prepared || tpl.normalize === false), autoOrient: false, ...tplCurve };
    }
    // A DIFFERENT logo swapped into the zone must be fitted into the zone's real placement.
    if (baked) {
      const zb = zoneBox[p.slot];
      if (!zb) return { ...p, position: [0, 0, 0], rotation: [0, 0, 0], scale: 0.0001 }; // wait for measurement
      // Prefer the reverse-engineered fit (real curvature); fall back to the AABB heuristic.
      let euler, front, radius;
      if (zb.fit) {
        euler = zb.fit.euler; front = zb.fit.front; radius = zb.fit.radius;
      } else {
        const ob = orientFromBox(zb.center, zb.size);
        euler = ob.euler; front = ob.front;
        const c0 = new THREE.Vector3(zb.center[0], zb.center[1], zb.center[2]);
        radius = c0.clone().sub(ob.finger.clone().multiplyScalar(c0.dot(ob.finger))).length() || 1;
      }
      const c = new THREE.Vector3(zb.center[0], zb.center[1], zb.center[2]);
      let bendY = Math.min(0.9, zb.m / (Math.PI * (radius || 1))) * (tpl.zone_bend_mul ?? 1);
      const off = Math.max(0.05, zb.m * 0.18);
      const pos = [c.x + front.x * off, c.y + front.y * off, c.z + front.z * off];
      // Optional per-zone manual fine-tune saved by the admin (spin around the face / flip).
      let rot = euler;
      if (tpl.zone_rot90 || tpl.zone_flip) {
        const q = new THREE.Quaternion().setFromEuler(new THREE.Euler(euler[0], euler[1], euler[2]));
        if (tpl.zone_rot90) q.multiply(new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 0, 1), (tpl.zone_rot90 * Math.PI) / 2));
        if (tpl.zone_flip) q.multiply(new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI));
        const fe = new THREE.Euler().setFromQuaternion(q);
        rot = [fe.x, fe.y, fe.z];
      }
      return { ...p, position: pos, rotation: rot, scale: [zb.m, zb.m, 0.6], normalize: true, autoOrient: true, bendX: 0, bendY: -bendY, curvesY: 1 };
    }
    // Classic gizmo-defined zone: normalize the logo into the anchor's transform.
    return { ...p, position: nudge(tpl.position || [0, 0, 0]), rotation: tpl.rotation, scale: tpl.scale, normalize: true, autoOrient: true, ...tplCurve };
  };

  // Group enamel parts by group_id so united enamels share ONE color control.
  const enamelUnits = [];
  { const byG = {};
    enamelParts.forEach((p) => { const k = p.group_id || p.id; (byG[k] = byG[k] || []).push(p); });
    Object.values(byG).forEach((arr) => enamelUnits.push(arr)); }
  // Enamel summary for messages/cart. A united set (one unit) shows the color ONCE.
  const buildEnamelSummary = () => enamelUnits.map((unit) => {
    const first = unit[0];
    const hex = enamelColors[first.id] || first.color;
    if (!hex) return null;
    const cname = enamelPalette.find((s) => s.hex === hex)?.name || hex;
    return enamelUnits.length === 1 ? cname : `${first.name}: ${cname}`;
  }).filter(Boolean);

  const snapshot = () => { try { return glRef.current?.domElement?.toDataURL("image/png"); } catch { return null; } };
  const emptyZones = zones.filter((zn) => !hasLogo(zoneLogo[zn.slot]));
  const doAddToCart = async () => {
    setEmptyDialog(null);
    if (!embedded && !popupMode) {
      toast.info(t("customizer.previewInfo"));
      return;
    }
    const logoNames = zones.map((zn) => (hasLogo(zoneLogo[zn.slot]) || zoneLogo[zn.slot]?.personal) ? `${zn.label}: ${zoneLogo[zn.slot].name}${zoneMetal[zn.slot] ? ` (${metalNameById(zoneMetal[zn.slot])})` : ""}${zoneLogo[zn.slot].personal ? ` (${t("customizer.sumCustomerLogoTag")})` : ""}` : null).filter(Boolean);
    const enamelSummary = buildEnamelSummary();
    const partGold = goldPlating && product.gold_plating_enabled && t("customizer.goldPlating");
    const partCustomLogo = usesCustomLogo && product.customer_upload_price > 0 && `${t("customizer.sumCustomLogo")} (+$${Number(product.customer_upload_price).toFixed(2)})`;
    const partLogos = logoNames.length && `${t("customizer.sumLogos")}: ${logoNames.join(", ")}`;
    const partEnamel = enamelSummary.length && `${t("customizer.sumEnamel")}: ${enamelSummary.join(", ")}`;
    const partEngraving = engraving && `${t("customizer.sumEngraving")}: "${engraving}"`;
    const partComments = comments.trim() && `${t("customizer.sumComments")}: ${comments.trim()}`;
    // Stone sets: report the chosen color per set (a ring can have 2+ sets in different colors).
    const nameForHex = (hex) => (stonePalette.find((s) => s.hex === hex) || {}).name || hex;
    const stoneSummary = stoneSets.map((k) => (stoneSets.length > 1 ? `Set ${k}: ${nameForHex(stoneHexFor(k))}` : nameForHex(stoneHexFor(k))));
    const partStones = stoneSummary.length && `${t("customizer.stoneColor")}: ${stoneSummary.join(", ")}`;
    const summary = [`${t("customizer.sumMetal")}: ${metalColor.name}`, partGold, partCustomLogo, partLogos, partStones, partEnamel, partEngraving, ringSize && `${t("customizer.sumSize")}: ${ringSize}`, partComments].filter(Boolean).join(" · ");
    // Clean summary for the seller order: excludes metal/material and size (they go in their own fields).
    const summaryClean = [partGold, partCustomLogo, partLogos, partStones, partEnamel, partEngraving, partComments].filter(Boolean).join(" · ");
    const customLogoIds = Object.values(zoneLogo).filter((l) => l?.personal).map((l) => l.id);
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    const customLogoUrls = Object.values(zoneLogo).filter((l) => l?.personal && l.url).map((l) => (l.url.startsWith("http") ? l.url : backend + l.url));
    // Upload the design snapshot to get a short public URL the merchant can open from the order.
    let renderUrl = null;
    try {
      const snap = snapshot();
      if (snap) {
        const { data } = await api.post(`/public/products/${product.id}/render-snapshot`, { image: snap }, { withCredentials: false });
        renderUrl = data.url;
      }
    } catch { /* render link is best-effort */ }
    // Structured fields so the Wix cart/checkout shows a clean, readable summary.
    const cartFields = [
      { title: t("customizer.sumMetal"), value: metalColor.name },
      goldPlating && product.gold_plating_enabled ? { title: t("customizer.sumFinish"), value: t("customizer.goldPlating") } : null,
      logoNames.length ? { title: t("customizer.sumLogos"), value: logoNames.join(", ") } : null,
      stoneSummary.length ? { title: t("customizer.stoneColor"), value: stoneSummary.join(", ") } : null,
      enamelSummary.length ? { title: t("customizer.sumEnamel"), value: enamelSummary.join(", ") } : null,
      engraving ? { title: t("customizer.sumEngraving"), value: engraving } : null,
      ringSize ? { title: t("customizer.sumSize"), value: String(ringSize) } : null,
      usesCustomLogo && product.customer_upload_price > 0 ? { title: t("customizer.sumCustomLogo"), value: `+$${Number(product.customer_upload_price).toFixed(2)}` } : null,
      comments.trim() ? { title: t("customizer.sumComments"), value: comments.trim() } : null,
      promoPercent > 0 ? { title: t("customizer.discount"), value: `−$${discountAmount.toFixed(2)} (${promoPercent}%)` } : null,
    ].filter(Boolean);
    if (renderUrl) cartFields.push({ title: t("customizer.sumDesign3d"), value: renderUrl });
    const cartTarget = popupMode ? (window.opener || window.parent) : window.parent;
    cartTarget.postMessage({ type: "ADD_TO_WIX_CART", payload: { productId: product.id, productName: product.name, wixProductId: product.wix_product_id || null, appId: product.wix_stores_app_id, quantity: 1, price: netSubtotal, priceFormatted: Number(netSubtotal).toFixed(2), engravingPrice: engravingCharge, promoCode: promoCode || null, discountPercent: promoPercent || 0, customizationText: summary, customizationTextClean: summaryClean, size: ringSize ? String(ringSize) : "", material: metalColor.name || "", customTextFields: cartFields, comments: comments.trim() || null, customLogoIds, customLogoUrls, renderImageUrl: renderUrl, snapshotUrl: renderUrl } }, "*");
    // Wait for the store page (Velo) to confirm the item actually reached the cart,
    // instead of showing success just because we posted the message.
    const acked = await new Promise((resolve) => {
      const onReply = (ev) => {
        let d = ev?.data;
        if (typeof d === "string") { try { d = JSON.parse(d); } catch { return; } }
        const t = d?.type;
        if (t === "ADD_TO_WIX_CART_SUCCESS" || t === "WIX_CART_ADD_SUCCESS") { window.removeEventListener("message", onReply); resolve(true); }
        else if (t === "ADD_TO_WIX_CART_ERROR" || t === "WIX_CART_ADD_ERROR") { window.removeEventListener("message", onReply); resolve(false); }
      };
      window.addEventListener("message", onReply);
      setTimeout(() => { window.removeEventListener("message", onReply); resolve(null); }, 8000);
    });
    if (acked === true) {
      toast.success(t("customizer.addedToCart"));
      // In popup mode, return the shopper to the store after a short beat.
      if (popupMode) setTimeout(() => { try { window.close(); } catch { /* noop */ } }, 1400);
    }
    else if (acked === false) toast.error(t("customizer.storeRejected"));
    else if (popupMode) toast.message(t("customizer.designSentPopup"));
    else toast.message(t("customizer.designSent"));
  };
  const buildStoreSummary = () => {
    const logoNames = zones.map((zn) => (hasLogo(zoneLogo[zn.slot]) || zoneLogo[zn.slot]?.personal) ? `${zn.label}: ${zoneLogo[zn.slot].name}` : null).filter(Boolean);
    const enamelSummary = buildEnamelSummary();
    const nameForHex = (hex) => (stonePalette.find((s) => s.hex === hex) || {}).name || hex;
    const stoneSummary = stoneSets.map((k) => (stoneSets.length > 1 ? `Set ${k}: ${nameForHex(stoneHexFor(k))}` : nameForHex(stoneHexFor(k))));
    return [
      `${t("customizer.sumMetal")}: ${metalColor.name}`,
      goldPlating && product.gold_plating_enabled && t("customizer.goldPlating"),
      logoNames.length && `${t("customizer.sumLogos")}: ${logoNames.join(", ")}`,
      stoneSummary.length && `${t("customizer.stoneColor")}: ${stoneSummary.join(", ")}`,
      enamelSummary.length && `${t("customizer.sumEnamel")}: ${enamelSummary.join(", ")}`,
      engraving && `${t("customizer.sumEngraving")}: "${engraving}"`,
      ringSize && `${t("customizer.sumSize")}: ${ringSize}`,
      comments.trim() && `${t("customizer.sumComments")}: ${comments.trim()}`,
    ].filter(Boolean).join(" · ");
  };

  const addToStoreCart = async () => {
    if (!priceReady) { toast.error(needsSize ? t("customizer.chooseMetalSizeContinue") : t("customizer.chooseMetalContinue")); return; }
    if (emptyZones.length > 0) { setEmptyDialog(emptyZones.map((zn) => zn.label)); return; }
    try {
      const token = localStorage.getItem(`hephy_cart_${storeSlug}`);
      const customLogoIds = Object.values(zoneLogo).filter((l) => l?.personal).map((l) => l.id);
      const customLogoUrls = Object.values(zoneLogo).filter((l) => (photoMode || l?.personal) && l?.url).map((l) => assetUrl(l.url));
      // Capture a snapshot of the finished design so the merchant sees the garment in the order email.
      let renderUrl = null;
      try {
        const snap = snapshot();
        if (snap) {
          const { data: sd } = await api.post(`/public/products/${product.id}/render-snapshot`, { image: snap }, { withCredentials: false });
          renderUrl = sd.url;
        }
      } catch { /* snapshot is best-effort */ }
      const { data } = await api.post("/public/cart/add", {
        token: token || undefined, slug: storeSlug, product_id: product.id,
        metal_id: selMetal.id, size: String(ringSize || ""), gold_plating: !!(goldPlating && product.gold_plating_enabled),
        uses_custom_logo: !!usesCustomLogo, custom_logo_ids: customLogoIds, custom_logo_urls: customLogoUrls,
        render_image_url: renderUrl, customization_text: buildStoreSummary(), quantity: 1,
      }, { withCredentials: false });
      localStorage.setItem(`hephy_cart_${storeSlug}`, data.token);
      toast.success(t("customizer.addedShort"));
      setTimeout(() => { window.location.href = `/tienda/${storeSlug}/carrito`; }, 700);
    } catch (e) { toast.error(apiError(e)); }
  };

  const requestDesign = async () => {
    const store = product.store || {};
    // Open the target window synchronously (before await) so the popup blocker allows it.
    let win = null;
    if (store.whatsapp) { try { win = window.open("about:blank", "_blank"); } catch { win = null; } }
    const logoNames = zones.map((zn) => (hasLogo(zoneLogo[zn.slot]) || zoneLogo[zn.slot]?.personal) ? `${zn.label}: ${zoneLogo[zn.slot].name}` : null).filter(Boolean);
    const enamelSummary = buildEnamelSummary();
    // Snapshot the design so the merchant sees the photo (as a link) in the message.
    let renderUrl = null;
    try {
      const snap = snapshot();
      if (snap) {
        const { data } = await api.post(`/public/products/${product.id}/render-snapshot`, { image: snap }, { withCredentials: false });
        renderUrl = data.url;
      }
    } catch { /* photo link is best-effort */ }
    const lines = [
      `${product.name}`,
      `${t("customizer.sumMetal")}: ${metalColor.name}`,
      goldPlating && product.gold_plating_enabled && `${t("customizer.sumFinish")}: ${t("customizer.goldPlating")}`,
      logoNames.length && `${t("customizer.sumLogos")}: ${logoNames.join(", ")}`,
      enamelSummary.length && `${t("customizer.sumEnamel")}: ${enamelSummary.join(", ")}`,
      engraving && `${t("customizer.sumEngraving")}: "${engraving}"`,
      ringSize && `${t("customizer.sumSize")}: ${ringSize}`,
      priceReady && !hasBreakdown && `${t("customizer.price")}: $${price.toFixed(2)}`,
      priceReady && hasBreakdown && `${t("customizer.subtotal")}: $${price.toFixed(2)}`,
      priceReady && hasBreakdown && promoPercent > 0 && `${t("customizer.discount")} (${promoPercent}%): -$${discountAmount.toFixed(2)}`,
      priceReady && hasBreakdown && storeTaxRate > 0 && `${t("customizer.tax")} (${storeTaxRate}%): $${taxAmount.toFixed(2)}`,
      priceReady && hasBreakdown && storeShipping > 0 && `${t("customizer.shipping")}: $${storeShipping.toFixed(2)}`,
      priceReady && hasBreakdown && `${t("customizer.total")}: $${totalWithTax.toFixed(2)}`,
      comments.trim() && `${t("customizer.sumComments")}: ${comments.trim()}`,
      renderUrl && `${t("customizer.sumDesign3d")}: ${renderUrl}`,
    ].filter(Boolean);
    const subject = `${t("customizer.requestDesign")}: ${product.name}`;
    const body = `${lines.join("\n")}`;
    if (store.whatsapp) {
      const num = String(store.whatsapp).replace(/[^0-9]/g, "");
      const url = `https://wa.me/${num}?text=${encodeURIComponent(body)}`;
      if (win) { win.location.href = url; } else { window.open(url, "_blank"); }
    } else if (store.contact_email) {
      window.location.href = `mailto:${store.contact_email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    } else {
      if (win) { try { win.close(); } catch { /* noop */ } }
      toast.info(t("customizer.saveDesignContact"));
    }
  };

  const addToCart = () => {
    if (emptyZones.length > 0) { setEmptyDialog(emptyZones.map((zn) => zn.label)); return; }    doAddToCart();
  };

  // Admin: write the gizmo transform of the active zone back into its template part.
  const commitZoneEdit = () => {
    const o = zoneEditRef.current;
    const tpl = slotTemplates[activeZone];
    if (!o || !tpl) return;
    setParts((prev) => prev.map((pt) => pt.id === tpl.id
      ? { ...pt, position: [o.position.x, o.position.y, o.position.z], rotation: [o.rotation.x, o.rotation.y, o.rotation.z], scale: [o.scale.x, o.scale.y, o.scale.z] }
      : pt));
  };
  const adjustZone = (fn) => {
    const tpl = slotTemplates[activeZone];
    if (!tpl) return;
    setParts((prev) => prev.map((pt) => (pt.id === tpl.id ? { ...pt, ...fn(pt) } : pt)));
  };
  const saveAdmin = async () => {
    setSaving(true);
    try { await api.put(`/products/${product.id}`, { model_parts: parts, view_rotation_y: viewRotDeg }); toast.success(t("customizer.changesSaved")); }
    catch { toast.error(t("customizer.saveFailed")); }
    finally { setSaving(false); }
  };

  return (
    <div className={`min-h-screen lg:h-screen bg-[#f6f5f2] text-obsidian lg:overflow-hidden ${showcase ? "asm-showcase" : ""}`} data-testid="assembly-viewer">
      <div className="flex flex-col lg:flex-row lg:h-screen">
        <div className="contents lg:flex lg:flex-col lg:items-center lg:w-[460px] lg:shrink-0 lg:h-screen lg:overflow-y-auto lg:bg-gradient-to-b lg:from-neutral-100 lg:to-neutral-300 lg:px-6 lg:py-8 lg:gap-4" data-testid="asm-left">
          <div className="relative sticky top-0 z-20 lg:static w-full flex justify-center bg-[#f6f5f2] lg:bg-transparent py-4 lg:py-0" data-testid="asm-stage">
          <div className={`relative overflow-hidden shadow-inner ring-1 ring-black/5 bg-[#e9e7e3] shrink-0 ${adminFullscreen ? "fixed inset-0 z-[55] w-screen h-screen max-w-none rounded-none" : "w-[350px] max-w-[88vw] aspect-square rounded-2xl"}`} data-testid="asm-canvas-box">
          {photoMode ? (
            (product.gallery_urls?.[0] || product.base_url)
              ? <img src={assetUrl(product.gallery_urls?.[0] || product.base_url)} alt={product.name} className="w-full h-full object-cover" data-testid="asm-photo" />
              : <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-neutral-400 px-6 text-center" data-testid="asm-photo-empty"><Sparkle size={30} className="text-gold" /><span className="font-serif text-lg text-obsidian">{product.name}</span></div>
          ) : (<>
          <Canvas camera={{ position: [0.2, 2.5, 2.0], fov: 45 }} gl={{ preserveDrawingBuffer: true, antialias: true }}
            onCreated={({ gl }) => { glRef.current = gl; }} style={{ width: "100%", height: "100%" }}>
            <color attach="background" args={["#e9e7e3"]} />
            <ambientLight intensity={0.5} />
            <directionalLight position={[5, 8, 5]} intensity={1.6} />
            <directionalLight position={[-6, 3, -4]} intensity={0.9} />
            <Environment resolution={256} frames={1}>
              <color attach="background" args={["#1a1a22"]} />
              <Lightformer form="rect" intensity={4} position={[0, 5, -6]} scale={[12, 7, 1]} />
              <Lightformer form="rect" intensity={3} position={[-6, 1, 4]} scale={[9, 7, 1]} />
              <Lightformer form="rect" intensity={2.5} position={[6, 2, 3]} scale={[7, 7, 1]} />
            </Environment>
            <Suspense fallback={<Html center><CircleNotch className="animate-spin" size={26} /></Html>}>
              <Bounds fit clip observe margin={showcase ? 1.05 : 1.4}>
                <group>
                  <group ref={partsRef} rotation={[0, (viewRotDeg * Math.PI) / 180, 0]}>
                    {visible.map((p) => {
                      const mp = matPropsFor(p);
                      const isActiveZoneLogo = adminEdit && p.role === "logo" && p.slot === activeZone;
                      if (isActiveZoneLogo) {
                        const tpl = slotTemplates[p.slot] || {};
                        return (
                          <group key={p.id}>
                            <group ref={zoneEditRef} position={tpl.position || [0, 0, 0]} rotation={tpl.rotation || [0, 0, 0]} scale={tpl.scale || 1}>
                              <Part3D part={{ ...p, position: [0, 0, 0], rotation: [0, 0, 0], scale: tpl.scale, bend: tpl.bend, bendAxis: tpl.bendAxis, bendX: tpl.bendX, bendY: tpl.bendY, bendZ: tpl.bendZ, curvesX: tpl.curvesX, curvesY: tpl.curvesY, curvesZ: tpl.curvesZ, reliefDepth: tpl.reliefDepth, taperTop: tpl.taperTop, taperBottom: tpl.taperBottom, taperLeft: tpl.taperLeft, taperRight: tpl.taperRight, sizeMode: tpl.sizeMode, fitAxis: tpl.fitAxis, ratioW: tpl.ratioW, ratioH: tpl.ratioH, absW: tpl.absW, absH: tpl.absH, absD: tpl.absD, sizeUnit: tpl.sizeUnit }} colorOverride={colorFor(p)} metalnessOverride={mp?.m} roughnessOverride={mp?.r} />
                            </group>
                          </group>
                        );
                      }
                      const ep = effPart(p);
                      const clickZone = p.role === "logo" ? (e) => { e.stopPropagation(); setActiveZone(p.slot); } : undefined;
                      return (
                      <group key={p.id}>
                        {p.role === "logo" && (
                          // Invisible larger hit-area so the customer can tap NEAR a thin logo
                          // (hard to hit precisely) to select its zone and swap the logo.
                          <mesh position={ep.position || [0, 0, 0]} onClick={clickZone}
                            onPointerOver={(e) => { e.stopPropagation(); document.body.style.cursor = "pointer"; }}
                            onPointerOut={() => { document.body.style.cursor = "auto"; }}>
                            <sphereGeometry args={[0.34, 12, 12]} />
                            <meshBasicMaterial transparent opacity={0} depthWrite={false} colorWrite={false} />
                          </mesh>
                        )}
                        <group position={ep.position || [0, 0, 0]} rotation={ep.rotation || [0, 0, 0]} scale={ep.scale || 1}
                          onClick={clickZone}
                          onPointerOver={p.role === "logo" ? (e) => { e.stopPropagation(); document.body.style.cursor = "pointer"; } : undefined}
                          onPointerOut={p.role === "logo" ? () => { document.body.style.cursor = "auto"; } : undefined}>
                          {ep._flat ? <FlatDecal url={ep.flat_image_url} /> : <Part3D part={ep} colorOverride={colorFor(p)} metalnessOverride={mp?.m} roughnessOverride={mp?.r} />}
                        </group>
                      </group>
                      );
                    })}
                    {adminEdit && slotTemplates[activeZone] && zoneLogo[activeZone]?.model_url && (
                      <TransformControls object={zoneEditRef} mode={editMode} size={0.7} onMouseUp={commitZoneEdit} />
                    )}
                    {zones.map((zn) => isBakedAnchor(zn.tpl) && zn.tpl.model_url
                      ? <ZoneAnchorProbe key={"probe-" + zn.slot} slot={zn.slot} tpl={zn.tpl} rootRef={partsRef} onMeasure={handleZoneBox} />
                      : null)}
                  </group>
                  <SizeProbe groupRef={partsRef} depKey={visibleKey} onSize={setDims} />
                  {showEngraving && engraving && dims && (() => {
                    const engR = dims.size.y * 0.34;
                    const flip = !!product.engraving_flip;
                    return (
                    <Text
                      position={[dims.center.x, flip ? dims.center.y + engR : dims.center.y - engR, dims.center.z]}
                      rotation={[flip ? Math.PI / 2 : -Math.PI / 2, 0, 0]}
                      curveRadius={flip ? -engR : engR}
                      fontSize={Math.max(dims.size.y * 0.04, 0.001)} font={font?.url} color="#222"
                      anchorX="center" anchorY="middle" maxWidth={dims.size.x * 0.55}
                      outlineWidth={dims.size.y * 0.0012} outlineColor="#000">
                      {engraving}
                    </Text>
                    );
                  })()}
                </group>
              </Bounds>
            </Suspense>
            <OrbitControls makeDefault enablePan={false} autoRotate={showcase || autoRotate} autoRotateSpeed={1.5} minDistance={1} maxDistance={40} />
          </Canvas>
          <div className="absolute top-2 left-1/2 -translate-x-1/2 text-[10px] bg-obsidian/70 text-white px-2.5 py-1 rounded-full flex items-center gap-1 whitespace-nowrap" data-testid="asm-drag-hint"><ArrowsClockwise size={12} /> {t("customizer.dragToRotate")}</div>
          </>)}
          </div>
          {isAdmin && !photoMode && (
            <div className={`flex flex-col gap-2 ${adminFullscreen ? "fixed top-4 right-4 z-[60] w-64" : "absolute top-3 left-3"}`} data-testid="asm-admin-tools">
              <button onClick={() => setAdminEdit((v) => !v)} data-testid="asm-admin-edit-toggle"
                className={`text-xs px-3 py-1.5 rounded-full border flex items-center gap-1.5 shadow ${adminEdit ? "bg-gold text-obsidian border-gold" : "bg-white/90 text-obsidian border-neutral-300 hover:border-neutral-400"}`}>
                <PencilSimple size={14} /> {adminEdit ? t("customizer.adminEditing") : t("customizer.adminEdit")}
              </button>
              {adminEdit && (
                <div className="bg-white/95 border border-neutral-300 rounded-lg p-3 flex flex-col gap-2 shadow-lg">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[10px] uppercase tracking-wide text-neutral-500 font-semibold">{t("customizer.zoneEditor")}</span>
                    <button onClick={() => setAdminFullscreen((v) => !v)} data-testid="asm-admin-fullscreen-toggle"
                      className="text-[10px] px-2 py-1 rounded border border-neutral-300 text-neutral-600 hover:border-gold hover:text-gold flex items-center gap-1">
                      {adminFullscreen ? <><ArrowsIn size={12} /> {t("customizer.exit")}</> : <><ArrowsOut size={12} /> {t("customizer.fullscreen")}</>}
                    </button>
                  </div>
                  <div>
                    <div className="text-[10px] text-neutral-500 mb-1">{t("customizer.zoneToEdit")}</div>
                    <select value={activeZone || ""} onChange={(e) => setActiveZone(e.target.value)} data-testid="asm-admin-zone-select"
                      className="w-full text-xs bg-white border border-neutral-300 rounded px-2 py-1.5 text-obsidian">
                      {zones.map((z) => <option key={z.slot} value={z.slot}>{z.label}</option>)}
                    </select>
                  </div>
                  <div className="flex gap-1">
                    {[["translate", ArrowsOutCardinal, t("customizer.move")], ["rotate", ArrowClockwise, t("customizer.rotate")], ["scale", ArrowsOut, t("customizer.scale")]].map(([m, Icon, lbl]) => (
                      <button key={m} onClick={() => setEditMode(m)} data-testid={`asm-admin-mode-${m}`}
                        className={`text-[10px] px-2 py-1 rounded border flex items-center gap-1 ${editMode === m ? "border-gold text-gold bg-gold/10" : "border-neutral-300 text-neutral-600"}`}>
                        <Icon size={12} /> {lbl}
                      </button>
                    ))}
                  </div>
                  <div>
                    <div className="text-[10px] text-neutral-500 mb-1">Frente de la pieza (girar toda la sortija)</div>
                    <div className="flex gap-1">
                      <button onClick={() => setViewRotDeg((d) => d - 45)} data-testid="asm-admin-rot-left"
                        className="flex-1 text-[10px] px-2 py-1 rounded border border-neutral-300 text-neutral-600 hover:border-gold hover:text-gold flex items-center justify-center gap-1"><ArrowClockwise size={12} style={{ transform: "scaleX(-1)" }} /> 45°</button>
                      <button onClick={() => setViewRotDeg((d) => d + 180)} data-testid="asm-admin-rot-flip"
                        className="flex-1 text-[10px] px-2 py-1 rounded border border-neutral-300 text-neutral-600 hover:border-gold hover:text-gold flex items-center justify-center gap-1"><ArrowsClockwise size={12} /> 180°</button>
                      <button onClick={() => setViewRotDeg((d) => d + 45)} data-testid="asm-admin-rot-right"
                        className="flex-1 text-[10px] px-2 py-1 rounded border border-neutral-300 text-neutral-600 hover:border-gold hover:text-gold flex items-center justify-center gap-1"><ArrowClockwise size={12} /> 45°</button>
                    </div>
                    <button onClick={() => setViewRotDeg(0)} data-testid="asm-admin-rot-reset" className="w-full mt-1 text-[10px] text-neutral-400 hover:text-neutral-600 underline">Restablecer</button>
                  </div>
                  {isBakedAnchor(slotTemplates[activeZone]) && (
                    <div>
                      <div className="text-[10px] text-neutral-500 mb-1">Ajuste del logo intercambiado (esta zona)</div>
                      <div className="flex gap-1">
                        <button onClick={() => adjustZone((pt) => ({ zone_rot90: (((pt.zone_rot90 || 0) + 1) % 4) }))} data-testid="asm-zone-adj-rot"
                          className="flex-1 text-[10px] px-2 py-1 rounded border border-neutral-300 text-neutral-600 hover:border-gold hover:text-gold flex items-center justify-center gap-1"><ArrowClockwise size={12} /> Girar 90°</button>
                        <button onClick={() => adjustZone((pt) => ({ zone_flip: !pt.zone_flip }))} data-testid="asm-zone-adj-flip"
                          className="flex-1 text-[10px] px-2 py-1 rounded border border-neutral-300 text-neutral-600 hover:border-gold hover:text-gold flex items-center justify-center gap-1"><ArrowsClockwise size={12} /> Voltear</button>
                      </div>
                      <div className="flex gap-1 mt-1">
                        <button onClick={() => adjustZone((pt) => ({ zone_bend_mul: Math.max(0, Math.round(((pt.zone_bend_mul ?? 1) - 0.15) * 100) / 100) }))} data-testid="asm-zone-adj-bend-minus"
                          className="flex-1 text-[10px] px-2 py-1 rounded border border-neutral-300 text-neutral-600 hover:border-gold hover:text-gold">Curvar −</button>
                        <button onClick={() => adjustZone((pt) => ({ zone_bend_mul: Math.min(2, Math.round(((pt.zone_bend_mul ?? 1) + 0.15) * 100) / 100) }))} data-testid="asm-zone-adj-bend-plus"
                          className="flex-1 text-[10px] px-2 py-1 rounded border border-neutral-300 text-neutral-600 hover:border-gold hover:text-gold">Curvar +</button>
                        <button onClick={() => adjustZone(() => ({ zone_rot90: 0, zone_flip: false, zone_bend_mul: 1 }))} data-testid="asm-zone-adj-reset"
                          className="text-[10px] px-2 py-1 rounded border border-neutral-300 text-neutral-400 hover:text-neutral-600">↺</button>
                      </div>
                    </div>
                  )}
                  <button onClick={saveAdmin} disabled={saving} data-testid="asm-admin-save"
                    className="text-xs px-3 py-1.5 rounded-full bg-obsidian text-white flex items-center justify-center gap-1.5 disabled:opacity-60">
                    {saving ? <CircleNotch size={13} className="animate-spin" /> : <FloppyDisk size={13} />} {t("customizer.saveChanges")}
                  </button>
                  <div className="text-[10px] text-neutral-400">{t("customizer.adminHint")}</div>
                </div>
              )}
            </div>
          )}
          </div>
          <div className="w-full max-w-[400px] mx-auto lg:mx-0 px-4 lg:px-0 flex flex-col gap-3" data-testid="asm-buybox">
            <div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-[11px] uppercase tracking-[0.2em] text-gold font-semibold flex items-center gap-1"><Cube size={13} /> {t(photoMode ? "customizer.assembleTitlePhoto" : "customizer.assembleTitle")}</span>
                {!photoMode && <label className="flex items-center gap-1.5 text-[11px] text-neutral-500 cursor-pointer whitespace-nowrap"><input type="checkbox" checked={autoRotate} onChange={(e) => setAutoRotate(e.target.checked)} data-testid="asm-autorotate" /> {t("customizer.autoRotate")}</label>}
              </div>
              <h2 className="font-serif text-2xl tracking-tight mt-0.5" data-testid="asm-product-name">{product.name}</h2>
            </div>
            <div className="flex flex-col gap-3">
              <div>
                <div className="text-xs text-neutral-500">{t("customizer.price")}</div>
                {priceReady
                  ? (hasBreakdown
                    ? <div data-testid="asm-price">
                        <div className="text-2xl font-serif">${totalWithTax.toFixed(2)}</div>
                        <div className="mt-1 space-y-0.5 text-[11px] text-neutral-500 max-w-[240px]">
                          <div className="flex justify-between"><span>{t("customizer.subtotal")}</span><span data-testid="asm-price-subtotal">${price.toFixed(2)}</span></div>
                          {promoPercent > 0 && <div className="flex justify-between text-emerald-600"><span>{t("customizer.discount")} ({promoPercent}%)</span><span data-testid="asm-price-discount">−${discountAmount.toFixed(2)}</span></div>}
                          {storeTaxRate > 0 && <div className="flex justify-between"><span>{t("customizer.tax")} ({storeTaxRate}%)</span><span data-testid="asm-price-tax">${taxAmount.toFixed(2)}</span></div>}
                          {storeShipping > 0 && <div className="flex justify-between"><span>{t("customizer.shipping")}</span><span data-testid="asm-price-shipping">${storeShipping.toFixed(2)}</span></div>}
                        </div>
                      </div>
                    : <div className="text-2xl font-serif" data-testid="asm-price">${price.toFixed(2)}</div>)
                  : <div className="text-sm text-neutral-500 max-w-[240px]" data-testid="asm-price-locked">{needsSize ? t("customizer.priceLockedMetalSize") : t("customizer.priceLockedMetal")}</div>}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {storeMode
                  ? <>
                      <Button onClick={addToStoreCart} className="rounded-full bg-obsidian hover:bg-obsidian/90 text-white font-semibold px-5 shrink-0" data-testid="asm-store-cart-btn"><ShoppingCart size={16} className="mr-1.5" /> {t("customizer.addToCart")}</Button>
                      {!product.store?.checkout_enabled && <Button onClick={requestDesign} variant="outline" className="rounded-full border-obsidian/30 text-obsidian hover:bg-obsidian/5 font-semibold px-5 shrink-0" data-testid="asm-request-btn"><WhatsappLogo size={16} className="mr-1.5" /> {t("customizer.requestDesign")}</Button>}
                    </>
                  : (!demo && <Button onClick={addToCart} className="rounded-full bg-obsidian hover:bg-obsidian/90 text-white font-semibold px-5 shrink-0" data-testid="asm-cart-btn"><ShoppingCart size={16} className="mr-1.5" /> {t("customizer.addToCart")}</Button>)}
              </div>
            </div>
            {storeMode
              ? <div className="flex items-center justify-between gap-2">
                  <p className="text-[11px] text-neutral-500" data-testid="asm-store-note">{product.store?.checkout_enabled ? t("customizer.storeCheckoutNote") : t("customizer.storeRequestNote")}</p>
                  {storeSlug && <a href={`/tienda/${storeSlug}`} className="text-[11px] text-gold hover:underline shrink-0">{t("customizer.backToStore")}</a>}
                </div>
              : demo
              ? <p className="text-[11px] text-neutral-500" data-testid="asm-demo-note">{t("customizer.demoNote")}</p>
              : (!embedded && <p className="text-[11px] text-neutral-500">{t("customizer.previewNote")}</p>)}
          </div>
        </div>

        <div className="flex-1 lg:h-screen lg:overflow-y-auto p-6 lg:p-10 space-y-6" data-testid="asm-panel">

          {(universityCollections.length > 0 || product.size_enabled) && (
            <div className="grid grid-cols-2 gap-4">
              {universityCollections.length > 0 && (
                <div>
                  <div className="text-sm font-semibold mb-2 flex items-center gap-1.5"><FolderSimple size={16} className="text-gold" /> {t("customizer.university")}</div>
                  <Select value={university} onValueChange={setUniversity}>
                    <SelectTrigger className="bg-white border-neutral-300" data-testid="asm-university-select"><SelectValue placeholder={t("customizer.chooseSchool")} /></SelectTrigger>
                    <SelectContent>{universityCollections.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                  {!university && <p className="text-[11px] text-neutral-500 mt-1">{t("customizer.chooseSchoolHint")}</p>}
                </div>
              )}
              {product.size_enabled && (
                <div>
                  <div className="text-sm font-semibold mb-2 flex items-center gap-2"><Ruler size={16} className="text-gold" /> {t("customizer.size")}{product.size_unit ? <span className="text-neutral-500 font-normal">({product.size_unit})</span> : null}</div>
                  {product.size_free_text ? (
                    <Input value={ringSize} onChange={(e) => setRingSize(e.target.value)} placeholder={product.size_unit ? `${t("customizer.sizeFreePlaceholder")} (${product.size_unit})` : t("customizer.sizeFreePlaceholder")} className="bg-white border-neutral-300" data-testid="asm-size-input" />
                  ) : (
                    <Select value={ringSize} onValueChange={setRingSize}>
                      <SelectTrigger className="bg-white border-neutral-300" data-testid="asm-size-select"><SelectValue placeholder={t("customizer.sizeChoosePlaceholder")} /></SelectTrigger>
                      <SelectContent>{(product.size_options || []).map((s) => <SelectItem key={s} value={s}>{s}{product.size_unit ? ` ${product.size_unit}` : ""}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                  {selMetal.market && sizeEntered && !(sizeNum > 0) && <p className="text-[11px] text-amber-600 mt-1">{t("customizer.sizeNumericHint")}</p>}
                </div>
              )}
            </div>
          )}

          {product.dimensions && (product.dimensions.height != null || product.dimensions.width != null || product.dimensions.depth != null) && (
            <div data-testid="asm-dimensions">
              <div className="text-sm font-semibold mb-1 flex items-center gap-2"><Ruler size={16} className="text-gold" /> {t("customizer.dimensions")}</div>
              <p className="text-sm text-neutral-600">
                {[product.dimensions.height, product.dimensions.width, product.dimensions.depth].filter((x) => x != null && x !== "").join(" × ")} {product.dimensions.unit || "cm"}
              </p>
            </div>
          )}

          <div>
            <div className="text-sm font-semibold mb-2">{t("customizer.material")}</div>
            <div className="flex flex-wrap gap-2">
              {metals.map((m) => (
                <button key={m.id} onClick={() => { setMetalId(m.id); setMetalPicked(true); }} data-testid={`asm-metal-${m.id}`}
                  className={`px-3 py-1.5 rounded-full border text-sm flex items-center gap-1.5 ${metalId === m.id && metalPicked ? "border-gold bg-gold/10" : "border-neutral-300 bg-white hover:border-neutral-400"}`}>
                  <span className="w-3.5 h-3.5 rounded-full border border-black/10" style={{ background: m.hex }} /> {m.name}
                  {metalId === m.id && metalPicked && <Check size={13} className="text-gold" />}
                </button>
              ))}
            </div>
          </div>

          {stoneSets.length > 0 && stoneSets.map((setKey, idx) => (
            <div key={setKey} data-testid={`asm-stone-set-${setKey}`}>
              <div className="text-sm font-semibold mb-2">
                {stoneSets.length > 1 ? `${t("customizer.stoneColor")} — Set ${setKey}` : t("customizer.stoneColor")}
              </div>
              <div className="flex flex-wrap gap-2">
                {stonePalette.map((s) => (
                  <button key={s.hex} onClick={() => setStoneColors((prev) => ({ ...prev, [setKey]: s.hex }))} data-testid={`asm-stone-${setKey}-${s.name}`}
                    className={`px-3 py-1.5 rounded-full border text-sm flex items-center gap-1.5 ${stoneHexFor(setKey) === s.hex ? "border-gold bg-gold/10" : "border-neutral-300 bg-white hover:border-neutral-400"}`}>
                    <span className="w-3.5 h-3.5 rounded-full border border-black/10" style={{ background: s.hex }} /> {s.name}
                  </button>
                ))}
              </div>
            </div>
          ))}

          {enamelUnits.length > 0 && (
            <div>
              <div className="text-sm font-semibold mb-2 flex items-center gap-1.5"><PaintBrush size={15} className="text-gold" /> {t("customizer.enamelColor")}</div>
              <div className="space-y-3">
                {enamelUnits.map((unit) => {
                  const first = unit[0];
                  const label = (first.name || t("customizer.enamelDefault")).replace(/\s*\(copia\)/gi, "").trim();
                  const current = enamelColors[first.id] || first.color;
                  return (
                    <div key={first.id} data-testid={`asm-enamel-${first.id}`}>
                      <div className="text-xs text-neutral-500 mb-1">{label}{unit.length > 1 ? ` ${t("customizer.set", { n: unit.length })}` : ""}</div>
                      <div className="flex flex-wrap gap-2">
                        {enamelPalette.map((s) => (
                          <button key={s.hex} onClick={() => setEnamelColors({ ...enamelColors, ...Object.fromEntries(unit.map((m) => [m.id, s.hex])) })} title={s.name}
                            data-testid={`asm-enamel-opt-${first.id}-${s.name}`}
                            className={`w-7 h-7 rounded-full border-2 transition ${current === s.hex ? "border-gold scale-110" : "border-black/10 hover:border-neutral-400"}`}
                            style={{ background: s.hex }} />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {groupUnits.map((u) => (
            <div key={u.gid} data-testid={`asm-group-${u.gid}`}>
              <div className="text-sm font-semibold mb-2">{u.name} <span className="text-xs text-neutral-400 font-normal">{t("customizer.set", { n: u.arr.length })}</span></div>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => setSel({ ...sel, ["group:" + u.gid]: NONE })} className={`px-3 py-1.5 rounded-full border text-sm ${sel["group:" + u.gid] === NONE ? "border-gold bg-gold/10" : "border-neutral-300 bg-white hover:border-neutral-400"}`}>{t("customizer.hide")}</button>
                <button onClick={() => setSel({ ...sel, ["group:" + u.gid]: u.gid })} data-testid={`asm-group-opt-${u.gid}`} className={`px-3 py-1.5 rounded-full border text-sm ${sel["group:" + u.gid] !== NONE ? "border-gold bg-gold/10" : "border-neutral-300 bg-white hover:border-neutral-400"}`}>{t("customizer.show", { name: u.name })}</button>
              </div>
            </div>
          ))}

          {zones.length > 0 && (
            <div data-testid="asm-zones">
              <div className="text-sm font-semibold mb-2 flex items-center gap-1.5"><Cube size={15} className="text-gold" /> {t("customizer.logosByZone")}</div>
              <p className="text-xs text-neutral-500 mb-2">{t(photoMode ? "customizer.logosByZoneHintPhoto" : "customizer.logosByZoneHint")}</p>
              <div className="flex flex-wrap gap-2 mb-3">
                {zones.map((zn) => {
                  const filled = hasLogo(zoneLogo[zn.slot]);
                  return (
                    <button key={zn.slot} onClick={() => setActiveZone(zn.slot)} data-testid={`asm-zone-tab-${zn.slot}`}
                      className={`px-3 py-1.5 rounded-full border text-sm flex items-center gap-1.5 ${activeZone === zn.slot ? "border-gold bg-gold/10" : "border-neutral-300 bg-white hover:border-neutral-400"}`}>
                      {zn.label}
                      {filled ? <Check size={13} className="text-green-600" /> : <span className="text-[10px] text-amber-600">{t("customizer.zoneEmpty")}</span>}
                    </button>
                  );
                })}
              </div>
              {activeZone && (() => {
                const tpl = slotTemplates[activeZone] || {};
                const allowed = tpl.allowed_logo_ids || [];
                const uploadEnabled = !!tpl.customer_upload_enabled;
                const filteredGroups = poolByCollection
                  .map((grp) => ({ ...grp, items: grp.items.filter((l) => !allowed.length || allowed.includes(l.id)) }))
                  .filter((grp) => grp.items.length);
                const groups = personalLogos.length
                  ? [{ cid: "__personal__", name: t("customizer.personalLogos"), items: personalLogos }, ...filteredGroups]
                  : filteredGroups;
                return (
                <div className="space-y-3">
                  {(tpl.allowed_metals || []).length > 0 && zoneLogo[activeZone]?.model_url && (
                    <div className="border border-neutral-200 rounded-lg p-2.5 bg-neutral-50" data-testid={`asm-zone-metal-${activeZone}`}>
                      <div className="text-xs font-medium text-neutral-700 mb-1.5">{t("customizer.zoneMetalTitle")}</div>
                      <div className="flex flex-wrap gap-1.5">
                        <button onClick={() => setZoneMetal((z) => { const n = { ...z }; delete n[activeZone]; return n; })}
                          data-testid={`asm-zone-metal-${activeZone}-inherit`}
                          className={`px-2.5 py-1 rounded-full border text-xs flex items-center gap-1.5 ${!zoneMetal[activeZone] ? "border-gold bg-gold/10" : "border-neutral-300 bg-white hover:border-neutral-400"}`}>
                          {t("customizer.sameAsRing")}{!zoneMetal[activeZone] && <Check size={12} className="text-gold" />}
                        </button>
                        {(tpl.allowed_metals || []).map((mid) => {
                          const m = metals.find((x) => x.id === mid);
                          if (!m) return null;
                          const on = zoneMetal[activeZone] === mid;
                          return (
                            <button key={mid} onClick={() => setZoneMetal((z) => ({ ...z, [activeZone]: mid }))}
                              data-testid={`asm-zone-metal-${activeZone}-${mid}`}
                              className={`px-2.5 py-1 rounded-full border text-xs flex items-center gap-1.5 ${on ? "border-gold bg-gold/10" : "border-neutral-300 bg-white hover:border-neutral-400"}`}>
                              <span className="inline-block w-3 h-3 rounded-full border border-neutral-300" style={{ background: m.hex }} />
                              {m.name}{on && <Check size={12} className="text-gold" />}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {mainFamily !== "metal" && product.logo_customer_color && zoneLogo[activeZone]?.model_url && (
                    <div className="border border-neutral-200 rounded-lg p-2.5 bg-neutral-50" data-testid={`asm-zone-logocolor-${activeZone}`}>
                      <div className="text-xs font-medium text-neutral-700 mb-1.5">{t("customizer.logoColorTitle")}</div>
                      <div className="flex items-center gap-2">
                        <input type="color"
                          value={product.logo_color_mode === "per_logo" ? (logoColors[activeZone] || product.logo_default_color || "#333333") : (logoGlobalColor || "#333333")}
                          onChange={(e) => product.logo_color_mode === "per_logo" ? setLogoColors((c) => ({ ...c, [activeZone]: e.target.value })) : setLogoGlobalColor(e.target.value)}
                          className="w-9 h-9 rounded border border-neutral-300 bg-transparent cursor-pointer" data-testid={`asm-logo-color-${activeZone}`} />
                        <span className="text-xs text-neutral-500">{product.logo_color_mode === "per_logo" ? t("customizer.logoColorPerNote") : t("customizer.logoColorGlobalNote")}</span>
                      </div>
                    </div>
                  )}
                  {groups.length === 0 && (
                    <div className="text-xs text-neutral-500 px-2">{universityCollections.length > 0 && !university ? t("customizer.chooseUniFirst") : t("customizer.noLogosZone")}</div>
                  )}
                  <div className="flex flex-wrap gap-2 items-stretch">
                    <button onClick={() => setZoneLogo({ ...zoneLogo, [activeZone]: null })} data-testid={`asm-zone-none-${activeZone}`}
                      className={`rounded-lg border w-24 h-[92px] flex flex-col items-center justify-center text-xs ${!zoneLogo[activeZone] ? "border-gold ring-2 ring-gold/30" : "border-neutral-300 hover:border-neutral-400"} bg-white`}>{t("customizer.none")}</button>
                    {uploadEnabled && (
                      <>
                        <input ref={fileInputRef} type="file" accept="image/*" className="hidden" data-testid="asm-custom-logo-input"
                          onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; uploadPersonalLogo(f); }} />
                        <button onClick={() => fileInputRef.current?.click()} disabled={uploading || personalLogos.length >= MAX_PERSONAL} data-testid="asm-custom-logo-btn"
                          className="rounded-lg border-2 border-dashed border-gold/60 w-24 h-[92px] flex flex-col items-center justify-center gap-1 text-[11px] text-neutral-600 hover:bg-gold/5 disabled:opacity-50">
                          {uploading ? <CircleNotch size={18} className="animate-spin text-gold" /> : <UploadSimple size={18} className="text-gold" />}
                          {t("customizer.uploadYourLogo")}
                          {product.customer_upload_price > 0 && <span className="text-[10px] text-neutral-500">+${Number(product.customer_upload_price).toFixed(0)}</span>}
                        </button>
                      </>
                    )}
                  </div>
                  {uploadEnabled && <p className="text-[11px] text-neutral-500 px-1">{t(photoMode ? "customizer.uploadHintPhoto" : "customizer.uploadHint", { max: MAX_PERSONAL })}</p>}
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="asm-collections-grid">
                  {groups.map((grp) => (
                    <div key={grp.cid} data-testid={`asm-zone-collection-${grp.cid}`}>
                      <div className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-1.5">{grp.name}</div>
                      <div className="grid grid-cols-3 gap-2">
                        {grp.items.map((l) => {
                          const active = zoneLogo[activeZone]?.id === l.id;
                          return (
                            <button key={l.id} onClick={() => setZoneLogo({ ...zoneLogo, [activeZone]: l })} data-testid={`asm-zone-logo-${activeZone}-${l.id}`}
                              className={`relative rounded-lg border overflow-hidden w-full ${active ? "border-gold ring-2 ring-gold/30" : "border-neutral-300 hover:border-neutral-400"} bg-white`}>
                              {l.thumb_url || l.url
                                ? <img src={assetUrl(l.thumb_url || l.url)} alt={l.name} className="w-full h-16 object-contain bg-neutral-50 p-1" />
                                : <LazyThumb className="w-full h-16 bg-neutral-50"><GlbThumb modelUrl={l.model_url} orient={l.orient} className="w-full h-16" /></LazyThumb>}
                              <div className="px-1.5 py-1 truncate text-center text-xs">{l.name}</div>
                              {l.personal && l.status === "processing" && <span className="absolute top-1 right-1 bg-obsidian/80 text-white rounded-full p-1"><CircleNotch size={10} className="animate-spin" /></span>}
                              {l.personal && l.status === "done" && <span className="absolute top-1 right-1 bg-green-600 text-white rounded-full p-0.5"><Check size={10} weight="bold" /></span>}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                  </div>
                </div>
                );
              })()}
            </div>
          )}


          {Object.entries(slots).map(([slot, arr]) => (
            <div key={slot} data-testid={`asm-slot-${slot}`}>
              <div className="text-sm font-semibold mb-2 capitalize">{slot}</div>
              <div className="flex flex-wrap gap-2">
                {arr.some((p) => p.optional) && (
                  <button onClick={() => setSel({ ...sel, [slot]: NONE })} data-testid={`asm-opt-none-${slot}`}
                    className={`rounded-lg border w-24 h-[92px] flex flex-col items-center justify-center text-xs ${sel[slot] === NONE ? "border-gold ring-2 ring-gold/30" : "border-neutral-300 hover:border-neutral-400"} bg-white`}>{t("customizer.none")}</button>
                )}
                {arr.map((p) => (
                  <button key={p.id} onClick={() => setSel({ ...sel, [slot]: p.id })} data-testid={`asm-opt-${p.id}`}
                    className={`rounded-lg border overflow-hidden w-24 ${sel[slot] === p.id ? "border-gold ring-2 ring-gold/30" : "border-neutral-300 hover:border-neutral-400"} bg-white`}>
                    {p.thumb_url
                      ? <img src={assetUrl(p.thumb_url)} alt={p.name} className="w-full h-16 object-contain bg-neutral-50 p-1" />
                      : (p.model_url || p.primitive)
                        ? <div className="w-full h-16 bg-neutral-50">{p.model_url ? <GlbThumb modelUrl={p.model_url} orient={p.orient} className="w-full h-full" /> : <LazyThumb className="w-full h-full"><PartThumb3D part={p} /></LazyThumb>}</div>
                        : <div className="w-full h-16 flex items-center justify-center bg-neutral-100"><Cube size={22} className="text-neutral-400" /></div>}
                    <div className="px-1.5 py-1 truncate text-center text-xs">{p.name}</div>
                  </button>
                ))}
              </div>
            </div>
          ))}

          {((product.engraving_enabled && mainFamily !== "fabric") || (product.gold_plating_enabled && mainFamily === "metal")) && (
            <div className="grid grid-cols-2 gap-4 items-start">
              {product.engraving_enabled && mainFamily !== "fabric" && (
                <div>
                  <div className="text-sm font-semibold mb-1 flex items-center gap-2"><NotePencil size={16} className="text-gold" /> {t("customizer.engraving")}</div>
                  <Input value={engraving} maxLength={product.engraving_max_length || 20} onChange={(e) => setEngraving(e.target.value)} placeholder={t("customizer.engravingPlaceholder")} className="bg-white border-neutral-300" data-testid="asm-engraving-input" />
                  {fonts.length > 1 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {fonts.map((f) => (
                        <button key={f.id} onClick={() => setFont(f)} data-testid={`asm-font-${f.id}`}
                          className={`px-3 py-1.5 rounded-full border text-sm ${font?.id === f.id ? "border-gold bg-gold/10" : "border-neutral-300 bg-white hover:border-neutral-400"}`}
                          style={{ fontFamily: f.css }}>{engraving || "Abc"}</button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {product.gold_plating_enabled && mainFamily === "metal" && (
                <div>
                  <div className="text-sm font-semibold mb-1 flex items-center gap-2"><Sparkle size={16} className="text-gold" weight="fill" /> {t("customizer.goldPlating")}</div>
                  <label className="flex items-center justify-between gap-2 text-sm bg-white border border-neutral-200 rounded-lg px-3 py-2.5 cursor-pointer" data-testid="asm-gold-plating">
                    <span className="flex items-center gap-2 text-neutral-700">{t("customizer.apply")}
                      {product.gold_plating_price > 0 && <span className="text-neutral-500">(+${Number(product.gold_plating_price).toFixed(2)})</span>}</span>
                    <input type="checkbox" checked={goldPlating} onChange={(e) => setGoldPlating(e.target.checked)} data-testid="asm-gold-plating-check" />
                  </label>
                </div>
              )}
            </div>
          )}

          {product.notes_enabled !== false && (
            <div data-testid="asm-comments">
              <div className="text-sm font-semibold mb-1 flex items-center gap-2"><NotePencil size={16} className="text-gold" /> {t("customizer.commentsTitle")}</div>
              <textarea value={comments} onChange={(e) => setComments(e.target.value)} rows={3} maxLength={600}
                placeholder={t("customizer.commentsPlaceholder")}
                className="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-gold/30"
                data-testid="asm-comments-input" />
            </div>
          )}


          <p className="text-[11px] leading-relaxed text-neutral-500 flex items-start gap-1.5 border-t border-neutral-200 pt-3" data-testid="asm-disclaimer">
            <Info size={14} className="text-neutral-400 shrink-0 mt-0.5" weight="fill" />
            <span>{t(photoMode ? "customizer.disclaimerPhoto" : "customizer.disclaimer")}</span>
          </p>

          </div>
      </div>

      <Dialog open={!!emptyDialog} onOpenChange={(o) => { if (!o) setEmptyDialog(null); }}>
        <DialogContent data-testid="asm-empty-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Warning size={20} className="text-amber-500" /> {t("customizer.emptyZonesTitle")}</DialogTitle>
            <DialogDescription>{t("customizer.emptyZonesDesc")}</DialogDescription>
          </DialogHeader>
          <div className="text-sm text-neutral-600">
            {emptyDialog?.length === 1 ? t("customizer.emptyZonesBodyOne", { zones: (emptyDialog || []).join(", ") }) : t("customizer.emptyZonesBodyMany", { zones: (emptyDialog || []).join(", ") })}
            <br />{t("customizer.emptyZonesQuestion")}
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setEmptyDialog(null)} data-testid="asm-empty-keep" className="rounded-full">{t("customizer.keepCustomizing")}</Button>
            <Button onClick={doAddToCart} data-testid="asm-empty-proceed" className="rounded-full bg-obsidian hover:bg-obsidian/90 text-white">{t("customizer.addAnyway")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
